import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DELETION_PATH, SUPPORT_EMAIL, SUPPORT_MAILTO, WHATSAPP_DISPLAY, WHATSAPP_URL } from '../config/support';
import { LANGUAGES, setSiteLanguage } from '../navigation/language';
import Logo from './ui/Logo';

// Section links go through "/#id", so they work from the home page, the legal pages and the 404 page
// (App's Home scrolls to a known hash on arrival).
const PRODUCT_LINKS: { to: string; labelKey: string }[] = [
  { to: '/#how-it-works', labelKey: 'nav.how' },
  { to: '/#calculator', labelKey: 'nav.fees' },
  { to: '/#where-we-send', labelKey: 'nav.where' },
  { to: '/#faq', labelKey: 'nav.faq' },
];

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();

  return (
    <footer className="site-footer surface-night">
      <div className="site-footer__inner">
        <div className="site-footer__grid">
          <div className="site-footer__brand">
            <Link to="/" aria-label="LulPay Home" className="ui-logo-link">
              <Logo size="lg" />
            </Link>
            <p className="site-footer__tagline">{t('footer.tagline')}</p>
            <div className="lang-switch" role="group" aria-label="Language">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className="lang-switch__button"
                  aria-pressed={i18n.language === lang.code}
                  aria-label={`Switch language to ${lang.name}`}
                  onClick={() => setSiteLanguage(i18n, lang.code)}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          <nav className="site-footer__column" aria-label={t('footer.product')}>
            <h2 className="site-footer__heading">{t('footer.product')}</h2>
            <ul>
              {PRODUCT_LINKS.map((link) => (
                <li key={link.to}>
                  <Link className="site-footer__link" to={link.to}>
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-footer__column">
            <h2 className="site-footer__heading">{t('footer.support')}</h2>
            <ul>
              <li>
                <a className="site-footer__link" href={SUPPORT_MAILTO}>
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li>
                <a className="site-footer__link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  {`WhatsApp ${WHATSAPP_DISPLAY}`}
                </a>
              </li>
              <li>
                <Link className="site-footer__link" to={DELETION_PATH}>
                  {t('footer.deleteAccount')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="site-footer__column">
            <h2 className="site-footer__heading">{t('footer.legal')}</h2>
            <ul>
              <li>
                <Link className="site-footer__link" to="/privacy">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link className="site-footer__link" to="/terms">
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="site-footer__bottom">
          <p>{t('footer.company')}</p>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
